import React, { useEffect, useState } from 'react';
import { useParams, useNavigate as useKeplerNavigate } from 'react-router';
import { useAppActions, useAppState } from '../../../overmind';
import { FetchType } from '../../../enums';
import { PagePath } from '../../../navigation/navigation.enums';
import { useForm } from 'react-hook-form';
import { Button, FieldController, PageHeader, RequiredValidator, TextField, Upload } from '@keplerco/core';
import { ContextDto } from '../../../overmind/actions/ai/ai-contexts';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';

export function FileContextPage(): JSX.Element {
  const { contextId } = useParams();
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const { control, handleSubmit, reset } = useForm();

  const [context, setContext] = useState<ContextDto>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | undefined>();
  const [fileContent, setFileContent] = useState<string>();

  async function getContext() {
    if (contextId === 'file' || !contextId) return;

    actions.startLoader({ path: PagePath.aiManagementContext, type: FetchType.PageFetching });

    const response = await actions.getContextById(contextId);
    setContext(response);
    setFileContent(response?.text);
    reset({
      label: response?.label,
    });

    actions.stopLoader(PagePath.aiManagementContext);
  }

  useEffect(() => {
    getContext();
  }, [contextId, actions, reset]);

  return (
    <PageLoaderLayer path={PagePath.aiManagementContext}>
      <div className="wrapper stack">
        <PageHeader
          breadcrumbs={[
            { name: 'AI management', url: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents.replace(':companySlug', companyVariables.slug!)}` },
            { name: 'Contexts', url: `${PagePath.aiManagementBase}${PagePath.aiManagementContexts.replace(':companySlug', companyVariables.slug!)}` },
            { name: contextId === 'file' ? 'Create file context' : `Edit ${context?.label ?? 'context'}`, url: '' },
          ]}
          title={contextId === 'file' ? 'Create file context' : `Edit ${context?.label || 'context'}`}
          subtitle="Create or edit a file context for your AI agents"
          divider
        />

        <form
          id="fileContext"
          style={{ marginTop: 24, width: '100%' }}
          onSubmit={handleSubmit(async fieldValues => {
            if (!companyVariables.slug) return;

            if (contextId === 'file' && !selectedFile) {
              setFileError('Select a file');
              return;
            }

            actions.startLoader({ path: PagePath.aiManagementContext, type: FetchType.Sending });

            let nextContextId;
            if (selectedFile) {
              nextContextId = await actions.upsertFileContext({
                searchParams: { label: fieldValues.label, id: contextId === 'file' ? undefined : contextId },
                file: selectedFile,
              });
            } else {
              nextContextId = await actions.upsertFileContext({
                searchParams: { label: fieldValues.label, id: contextId },
              });
            }

            if (!nextContextId) {
              actions.stopLoader(PagePath.aiManagementContext);
              return;
            }

            if (contextId === 'file') {
              keplerNavigate(`${PagePath.aiManagementBase}${PagePath.aiManagementContext.replace(':companySlug', companyVariables.slug).replace(':contextId', nextContextId)}`);
            } else {
              await getContext();
            }

            actions.stopLoader(PagePath.aiManagementContext);
          })}
        >
          <FieldController
            name="label"
            control={control}
            rules={new RequiredValidator('Enter a label')}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="text"
                label="Label"
                responsive
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid || !!fieldState.error,
                  message: fieldState.error?.message ?? 'Enter a label',
                }}
              />
            )}
          />

          <Upload
            description="Drop your file here or <strong>Browse</strong>"
            accept={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv']}
            error={fileError}
            onChange={files => {
              if (files.length === 0) {
                setSelectedFile(null);
                setFileError(undefined);
                return;
              }

              const file = files[0];
              setSelectedFile(file);
              setFileError(undefined);
            }}
          />

          {contextId !== 'file' && !selectedFile && <div style={{ marginTop: '8px' }}>Leave empty to keep the current file</div>}

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="button">Save</Button>
          </div>

          {!!fileContent && (
            <React.Fragment>
              <div>File content</div>
              <pre>{fileContent}</pre>
            </React.Fragment>
          )}
        </form>
      </div>
    </PageLoaderLayer>
  );
}
